import { BaseEntity } from ".";
import { IGet_killmails_killmail_id_killmail_hash_ok } from "../service/esiclient";
import { getCachedWarEntity } from "./WarEntityCache";
import { getCachedCorporationEntity } from "./CorporationEntityCache";
import { getCachedAllianceEntity } from "./AllianceEntityCache";
import { getCachedTypeEntity } from "./TypeEntityCache";
import moment from "moment";

export class KillmailEntity extends BaseEntity {
  constructor(id: number, props: any) {
    super(id, props);
    this._dataTypeName = "Kill Mail";
  }

  async toJson(): Promise<Excel.EntityCellValue> {
    const props: Partial<IGet_killmails_killmail_id_killmail_hash_ok> = this.props;

    var entity = await super.toJson();

    entity.text = props.killmail_id ? props.killmail_id.toString() : "";

    // Properties
    if (entity.properties) {
      if (props.killmail_time) {
        const killmail_time = moment(props.killmail_time);
        entity.properties["killmail_time"] = {
          type: Excel.CellValueType.formattedNumber,
          basicValue: killmail_time.unix() / 86400 + 25569,
          numberFormat: "[$-409]m/d/yy h:mm AM/PM;@",
        };
      }
      // - Attackers
      if (props.attackers !== undefined) {
        let elements = [];
        for (let i = 0; i < props.attackers.length; i++) {
          const attacker = props.attackers[i];
          const attackerEntity: Excel.EntityCellValue = {
            type: Excel.CellValueType.entity,
            text: "(blank)",
            properties: {},
          };
          // - corporation
          if (attackerEntity.properties) {
            if (attacker.corporation_id !== undefined) {
              const corporationEntity = await getCachedCorporationEntity(attacker.corporation_id);
              if (corporationEntity !== null) {
                attackerEntity.properties["attacker_corporation"] = await corporationEntity.toJson();
                attackerEntity.text = attackerEntity.properties["attacker_corporation"].text;
              }
            }
            // - alliance
            if (attacker.alliance_id !== undefined) {
              const allianceEntity = await getCachedAllianceEntity(attacker.alliance_id);
              if (allianceEntity !== null) {
                attackerEntity.properties["attacker_alliance"] = await allianceEntity.toJson();
                attackerEntity.text = attackerEntity.properties["attacker_alliance"].text;
              }
            }
            // - damage_done
            attackerEntity.properties["damage_done"] = {
              type: Excel.CellValueType.formattedNumber,
              basicValue: attacker.damage_done,
              numberFormat: "##0",
            };
            // - final_blow
            attackerEntity.properties["final_blow"] = {
              type: Excel.CellValueType.boolean,
              basicValue: attacker.final_blow,
            };
            // - security_status
            attackerEntity.properties["security_status"] = {
              type: Excel.CellValueType.double,
              basicValue: attacker.security_status,
            };
            // - ship_type
            if (attacker.ship_type_id !== undefined) {
              const shipTypeEntity = await getCachedTypeEntity(attacker.ship_type_id);
              if (shipTypeEntity !== null) {
                attackerEntity.properties["ship_type"] = await shipTypeEntity.toJson();
              }
            }
            // - weapon_type_id
            if (attacker.weapon_type_id !== undefined) {
              const weaponTypeEntity = await getCachedTypeEntity(attacker.weapon_type_id);
              if (weaponTypeEntity !== null) {
                attackerEntity.properties["weapon_type"] = await weaponTypeEntity.toJson();
              }
            }
            elements.push([attackerEntity]);
          }
        }
        entity.properties["attackers"] = {
          type: Excel.CellValueType.array,
          elements: elements,
        } as Excel.ArrayCellValue;
      }

      // - Victim
      if (entity.properties && props.victim !== undefined) {
        // - corporation
        if (props.victim.corporation_id !== undefined) {
          const corporationEntity = await getCachedCorporationEntity(props.victim.corporation_id);
          if (corporationEntity !== null) {
            entity.properties["victim_corporation"] = await corporationEntity.toJson();
          }
        }
        // - alliance
        if (props.victim.alliance_id !== undefined) {
          const allianceEntity = await getCachedAllianceEntity(props.victim.alliance_id);
          if (allianceEntity !== null) {
            entity.properties["victim_alliance"] = await allianceEntity.toJson();
          }
        }
        // - damage_taken
        entity.properties["damage_taken"] = {
          type: Excel.CellValueType.formattedNumber,
          basicValue: props.victim.damage_taken,
          numberFormat: "##0",
        };
        // - position
        if (props.victim.position !== undefined) {
          entity.properties["position"] = {
            type: Excel.CellValueType.entity,
            text: "(position)",
            properties: {
              x: {
                type: Excel.CellValueType.double,
                basicValue: props.victim.position.x,
              },
              y: {
                type: Excel.CellValueType.double,
                basicValue: props.victim.position.y,
              },
              z: {
                type: Excel.CellValueType.double,
                basicValue: props.victim.position.z,
              },
            },
          };
        }
        // Items
        if (props.victim.items !== undefined) {
          let elements = [];
          for (let i = 0; i < props.victim.items.length; i++) {
            const item = props.victim.items[i];
            const itemEntity: Excel.EntityCellValue = {
              type: Excel.CellValueType.entity,
              text: "(blank)",
              properties: {},
            };
            // - item_type
            const itemTypeEntity = await getCachedTypeEntity(item.item_type_id);
            if (itemEntity.properties) {
              if (itemTypeEntity !== null) {
                itemEntity.properties["item_type"] = await itemTypeEntity.toJson();
                itemEntity.text = itemEntity.properties["item_type"].text;
              }
              // - flag
              itemEntity.properties["flag"] = {
                type: Excel.CellValueType.formattedNumber,
                basicValue: item.flag,
                numberFormat: "###0",
              };
              // - singleton
              itemEntity.properties["singleton"] = {
                type: Excel.CellValueType.formattedNumber,
                basicValue: item.singleton,
                numberFormat: "###0",
              };
              // - quantity_destroyed
              if (item.quantity_destroyed !== undefined) {
                itemEntity.properties["quantity_destroyed"] = {
                  type: Excel.CellValueType.formattedNumber,
                  basicValue: item.quantity_destroyed,
                  numberFormat: "##0",
                };
              }
              // - quantity_dropped
              if (item.quantity_dropped !== undefined) {
                itemEntity.properties["quantity_dropped"] = {
                  type: Excel.CellValueType.formattedNumber,
                  basicValue: item.quantity_dropped,
                  numberFormat: "##0",
                };
              }
            }
            elements.push([itemEntity]);
          }
          entity.properties["victim_items"] = {
            type: Excel.CellValueType.array,
            elements: elements,
          } as Excel.ArrayCellValue;
        }
      }
      // - War
      if (props.war_id !== undefined) {
        const warEntity = await getCachedWarEntity(props.war_id);
        if (warEntity !== null) {
          entity.properties["war"] = await warEntity.toJson();
          delete entity.properties["war_id"];
        }
      }
    }
    // Layouts

    return entity;
  }
}
